import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import supabase from '../lib/supabase';
import toast from 'react-hot-toast';
import { useCallback } from 'react';

const OperatorContext = createContext();

export const useOperator = () => {
  const context = useContext(OperatorContext);
  if (!context) {
    throw new Error('useOperator must be used within an OperatorProvider');
  }
  return context;
};

export const OperatorProvider = ({ children }) => {
  const { user } = useAuth();
  const [operatorProfile, setOperatorProfile] = useState(null);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [assignedOrders, setAssignedOrders] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Small helper to ensure we have an operator profile (fetch or create)
  const ensureOperatorProfile = async () => {
    if (!user) return null;
    try {
      const { data, error } = await supabase
        .from('operators_a7b5c9')
        .select('*')
        .eq('user_id', user.id)
        .limit(1);
      
      if (error) throw error;
      const existing = Array.isArray(data) ? data[0] : data;
      if (existing) {
        setOperatorProfile(existing);
        setWalletBalance(existing.wallet_balance || 0);
        return existing;
      }
      const { data: createdArr, error: createError } = await supabase
        .from('operators_a7b5c9')
        .insert({
          user_id: user.id,
          full_name: user.name || 'Operator',
          email: user.email || null,
          phone: user.mobile || null,
          status: 'active',
          wallet_balance: 0,
          kyc_submitted: false,
          kyc_verified: false
        })
        .select()
        .limit(1);
      if (createError) throw createError;
      const created = Array.isArray(createdArr) ? createdArr[0] : createdArr;
      setOperatorProfile(created);
      setWalletBalance(created.wallet_balance || 0);
      toast.success('Operator profile created');
      return created;
    } catch (e) {
      console.error('ensureOperatorProfile error:', e);
      return null;
    }
  };
  
  // Fetch operator profile when user is logged in
  useEffect(() => {
    const fetchOperatorProfile = async () => {
      if (!user || user.role !== 'operator') {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        await ensureOperatorProfile();
      } catch (error) {
        console.error('Error fetching/creating operator profile:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOperatorProfile();
  }, [user]);
  
  // Fetch available orders
  const fetchAvailableOrders = useCallback(async () => {
    if (!user || user.role !== 'operator') return;
    try {
      const { data, error } = await supabase
        .from('available_orders_a7b5c9')
        .select('*')
        .eq('status', 'available')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAvailableOrders(data || []);
    } catch (error) {
      console.error('Error fetching available orders:', error);
      toast.error('Failed to load available orders');
    }
  }, [user?.id, user?.role]);

  // Realtime: refresh available orders whenever the table changes
  useEffect(() => {
    if (!user || user.role !== 'operator') return;
    const channel = supabase
      .channel('available-orders-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'available_orders_a7b5c9' },
        () => {
          fetchAvailableOrders && fetchAvailableOrders();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, user?.role, fetchAvailableOrders]);
  
  // Fetch assigned orders
  const fetchAssignedOrders = useCallback(async () => {
    if (!operatorProfile?.id) return;
    try {
      const { data, error } = await supabase
        .from('operator_orders_a7b5c9')
        .select('*')
        .eq('operator_id', operatorProfile.id)
        .order('assigned_at', { ascending: false });
      if (error) throw error;
      setAssignedOrders(data || []);
    } catch (error) {
      console.error('Error fetching assigned orders:', error);
      toast.error('Failed to load your assigned orders');
    }
  }, [operatorProfile?.id]);
  
  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    if (!operatorProfile?.id) return;
    try {
      const { data, error } = await supabase
        .from('operator_transactions_a7b5c9')
        .select('*')
        .eq('operator_id', operatorProfile.id)
        .order('transaction_date', { ascending: false });
      if (error) throw error;
      setTransactions(data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load your transactions');
    }
  }, [operatorProfile?.id]);
  
  // Accept an order
  const acceptOrder = async (orderId) => {
    let profile = operatorProfile;
    if (!profile) {
      profile = await ensureOperatorProfile();
    }
    if (!profile) {
      toast.error('Please complete your profile first');
      return null;
    }
    
    try {
      // Prevent accepting new orders until the current one is submitted for admin review
      const { data: inProgressArr, error: inProgressErr } = await supabase
        .from('operator_orders_a7b5c9')
        .select('order_id,status')
        .eq('operator_id', profile.id)
        .in('status', ['assigned', 'in_progress'])
        .limit(1);
      if (inProgressErr) throw inProgressErr;
      const inProgress = Array.isArray(inProgressArr) ? inProgressArr[0] : inProgressArr;
      if (inProgress) {
        toast.error('Please complete your current application (upload PDF and submit for review) before accepting a new order.');
        return null;
      }

      // Set deadline (3 hours from now)
      const deadline = new Date();
      deadline.setHours(deadline.getHours() + 3);
      // 1) Atomically mark the available order as assigned ONLY if still available
      const { data: updatedRowArr, error: assignErr } = await supabase
        .from('available_orders_a7b5c9')
        .update({ status: 'assigned' })
        .eq('order_id', orderId)
        .eq('status', 'available')
        .select('*')
        .limit(1);
      if (assignErr) throw assignErr;
      const updatedRow = Array.isArray(updatedRowArr) ? updatedRowArr[0] : updatedRowArr;
      if (!updatedRow) {
        toast.error('Order already taken by another operator');
        return null;
      }

      // 2) Upsert into operator_orders with details from the order we just claimed
      const upsertPayload = {
        order_id: orderId,
        operator_id: profile.id,
        customer_id: updatedRow.customer_id,
        customer_name: updatedRow.customer_name,
        service_type: updatedRow.service_type,
        application_fee: updatedRow.application_fee,
        service_fee: updatedRow.service_fee,
        documents: updatedRow.documents,
        status: 'assigned',
        assigned_at: new Date().toISOString(),
        deadline: deadline.toISOString()
      };
      const { data: upArr, error: upErr } = await supabase
        .from('operator_orders_a7b5c9')
        .upsert(upsertPayload, { onConflict: 'order_id' })
        .select()
        .limit(1);
      if (upErr) {
        // Attempt to revert available_orders status back to available if upsert fails
        await supabase
          .from('available_orders_a7b5c9')
          .update({ status: 'available' })
          .eq('order_id', orderId)
          .eq('status', 'assigned');
        throw upErr;
      }
      const inserted = Array.isArray(upArr) ? upArr[0] : upArr;
      if (!inserted) {
        // No row returned; treat as failure and revert available_orders
        await supabase
          .from('available_orders_a7b5c9')
          .update({ status: 'available' })
          .eq('order_id', orderId)
          .eq('status', 'assigned');
        toast.error('Failed to create assignment record. Please try again.');
        return null;
      }

      // Optimistically update local assignedOrders for immediate UI feedback
      setAssignedOrders(prev => {
        const list = Array.isArray(prev) ? prev : [];
        const rest = list.filter(o => (o.order_id || o.id) !== inserted.order_id);
        return [inserted, ...rest];
      });

      // Refresh assigned and available lists
      fetchAssignedOrders();
      fetchAvailableOrders();

      toast.success('Order accepted successfully!');
      return inserted;
    } catch (error) {
      console.error('Error accepting order:', error);
      toast.error(error?.message ? `Failed to accept order: ${error.message}` : 'Failed to accept order');
      return null;
    }
  };
  
  // Start processing an order
  const startProcessingOrder = async (orderId) => {
    if (!operatorProfile) return false;
    
    try {
      const { error } = await supabase
        .from('operator_orders_a7b5c9')
        .update({ 
          status: 'in_progress',
          processing_started_at: new Date().toISOString()
        })
        .eq('order_id', orderId)
        .eq('operator_id', operatorProfile.id);
        
      if (error) throw error;
      
      // Refresh assigned orders
      fetchAssignedOrders();
      
      toast.success('Order processing started!');
      return true;
    } catch (error) {
      console.error('Error starting order processing:', error);
      toast.error('Failed to start processing');
      return false;
    }
  };
  
  // Submit completed application PDF
  const submitCompletedApplication = async (orderId, pdfUrl) => {
    if (!operatorProfile) return false;
    
    try {
      // Enforce upload: only accept PDFs that were uploaded to our storage bucket
      const isPdf = typeof pdfUrl === 'string' && pdfUrl.toLowerCase().endsWith('.pdf');
      const isFromBucket = typeof pdfUrl === 'string' && pdfUrl.includes('/success_forms/');
      if (!isPdf || !isFromBucket) {
        toast.error('Please upload the application PDF before submitting');
        return false;
      }

      const { error } = await supabase
        .from('operator_orders_a7b5c9')
        .update({ 
          status: 'pdf_submitted',
          submitted_pdf_url: pdfUrl,
          completed_at: new Date().toISOString()
        })
        .eq('order_id', orderId)
        .eq('operator_id', operatorProfile.id);
        
      if (error) throw error;
      
      // Update available order status to Processing for admin review
      const { error: availError } = await supabase
        .from('available_orders_a7b5c9')
        .update({ 
          status: 'Processing',
          submitted_pdf_url: pdfUrl,
          submitted_at: new Date().toISOString()
        })
        .eq('order_id', orderId);
      
      if (availError) {
        console.warn('Unable to update available order status to Processing:', availError);
      }
      
      // Refresh assigned orders
      fetchAssignedOrders();
      
      toast.success('Application submitted for verification!');
      return true;
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application');
      return false;
    }
  };
  
  // Make application fee payment
  const makeApplicationFeePayment = async (orderId, amount, paymentMethod) => {
    if (!operatorProfile) return false;
    
    // Check if wallet has sufficient balance
    if (walletBalance < amount && paymentMethod === 'wallet') {
      toast.error('Insufficient wallet balance');
      return false;
    }
    
    try {
      // Generate transaction ID
      const transactionId = `TXN${Date.now()}`;
      
      // Update order payment status
      const { error } = await supabase
        .from('operator_orders_a7b5c9')
        .update({ 
          payment_status: 'paid',
          payment_transaction_id: transactionId
        })
        .eq('order_id', orderId)
        .eq('operator_id', operatorProfile.id);
        
      if (error) throw error;
      
      // If payment from wallet, deduct balance
      if (paymentMethod === 'wallet') {
        // Create transaction record
        const { error: txnError } = await supabase
          .from('operator_transactions_a7b5c9')
          .insert({
            operator_id: operatorProfile.id,
            order_id: orderId,
            amount: -amount, // negative for debit
            type: 'debit',
            description: `Application fee payment for order ${orderId}`,
            status: 'completed'
          });
          
        if (txnError) throw txnError;
        
        // Update operator wallet balance
        const { error: walletError } = await supabase
          .from('operators_a7b5c9')
          .update({ 
            wallet_balance: walletBalance - amount 
          })
          .eq('id', operatorProfile.id);
          
        if (walletError) throw walletError;
        
        // Update local wallet balance
        setWalletBalance(prev => prev - amount);
      }
      
      // Refresh transactions
      fetchTransactions();
      fetchAssignedOrders();
      
      toast.success('Payment successful!');
      return true;
    } catch (error) {
      console.error('Error making payment:', error);
      toast.error('Payment failed');
      return false;
    }
  };
  
  // Upload operator KYC documents
  const uploadKycDocuments = async (idProof, addressProof, profilePhoto, bankDetails) => {
    if (!operatorProfile) return false;
    
    try {
      // Upload ID proof
      const idProofPath = `kyc/${operatorProfile.id}/id_proof`;
      const { data: idProofData, error: idProofError } = await supabase.storage
        .from('operator_documents')
        .upload(idProofPath, idProof);
        
      if (idProofError) throw idProofError;
      
      // Upload address proof
      const addressProofPath = `kyc/${operatorProfile.id}/address_proof`;
      const { data: addressProofData, error: addressProofError } = await supabase.storage
        .from('operator_documents')
        .upload(addressProofPath, addressProof);
        
      if (addressProofError) throw addressProofError;
      
      // Upload profile photo
      const profilePhotoPath = `kyc/${operatorProfile.id}/profile_photo`;
      const { data: profilePhotoData, error: profilePhotoError } = await supabase.storage
        .from('operator_documents')
        .upload(profilePhotoPath, profilePhoto);
        
      if (profilePhotoError) throw profilePhotoError;
      
      // Get public URLs
      const idProofUrl = supabase.storage.from('operator_documents').getPublicUrl(idProofPath).data.publicUrl;
      const addressProofUrl = supabase.storage.from('operator_documents').getPublicUrl(addressProofPath).data.publicUrl;
      const profilePhotoUrl = supabase.storage.from('operator_documents').getPublicUrl(profilePhotoPath).data.publicUrl;
      
      // Save KYC data
      const { error } = await supabase
        .from('operator_kyc_a7b5c9')
        .insert({
          operator_id: operatorProfile.id,
          id_proof_url: idProofUrl,
          address_proof_url: addressProofUrl,
          profile_photo_url: profilePhotoUrl,
          bank_details: bankDetails
        });
        
      if (error) throw error;
      
      // Update operator status
      const { error: updateError } = await supabase
        .from('operators_a7b5c9')
        .update({ kyc_submitted: true })
        .eq('id', operatorProfile.id);
        
      if (updateError) throw updateError;
      
      toast.success('KYC documents submitted successfully!');
      return true;
    } catch (error) {
      console.error('Error uploading KYC documents:', error);
      toast.error('Failed to upload KYC documents');
      return false;
    }
  };
  
  // Fetch data on initial load
  useEffect(() => {
    if (operatorProfile) {
      fetchAvailableOrders();
      fetchAssignedOrders();
      fetchTransactions();
    }
  }, [operatorProfile, fetchAvailableOrders, fetchAssignedOrders, fetchTransactions]);

  const value = {
    operatorProfile,
    availableOrders,
    assignedOrders,
    transactions,
    walletBalance,
    loading,
    fetchAvailableOrders,
    fetchAssignedOrders,
    fetchTransactions,
    acceptOrder,
    startProcessingOrder,
    submitCompletedApplication,
    makeApplicationFeePayment,
    uploadKycDocuments
  };
  
  return (
    <OperatorContext.Provider value={value}>
      {children}
    </OperatorContext.Provider>
  );
};